// src/components/MobileQuickActions.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  X,
  Package,
  Wallet,
  Lock,
  Barcode,
  Box,
  Truck,
} from 'lucide-react';

const MobileQuickActions = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const quickActions = [
    {
      icon: Package,
      label: 'Add Item',
      description: 'Add to inventory',
      color: 'rgb(var(--accent-primary))',
      bgColor: 'rgb(var(--accent-primary) / 0.15)',
      action: () => {
        setIsOpen(false);
        navigate('/inventory', { state: { openAddItem: true } });
      },
    },
    {
      icon: Barcode,
      label: 'Scan Barcode',
      description: 'Quick scan',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.15)',
      action: () => {
        setIsOpen(false);
        navigate('/inventory', { state: { openBarcode: true } });
      },
    },
    {
      icon: Wallet,
      label: 'Add Expense',
      description: 'Track expense',
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.15)',
      action: () => {
        setIsOpen(false);
        navigate('/expenses', { state: { openAddExpense: true } });
      },
    },
    {
      icon: Lock,
      label: 'Add to Vault',
      description: 'Store in vault',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.15)',
      action: () => {
        setIsOpen(false);
        navigate('/vault', { state: { openAddVault: true } });
      },
    },
    {
      icon: Box,
      label: 'Add Package',
      description: 'Create package',
      color: '#06b6d4',
      bgColor: 'rgba(6, 182, 212, 0.15)',
      action: () => {
        setIsOpen(false);
        navigate('/packages', { state: { openAddPackage: true } });
      },
    },
  ];

  return createPortal(
    <>
      {/* Floating Action Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Close quick actions menu' : 'Open quick actions menu'}
        aria-label={isOpen ? 'Close quick actions' : 'Open quick actions'}
        className="lg:hidden fixed z-[1050] w-12 h-12 rounded-full shadow-lg flex items-center justify-center"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
          right: '16px',
          background: isOpen
            ? 'var(--color-card)'
            : 'linear-gradient(135deg, rgb(var(--accent-primary)), rgb(var(--accent-primary) / 0.8))',
          border: isOpen ? '1px solid var(--color-border)' : 'none',
          color: isOpen ? 'var(--color-text)' : '#fff',
        }}
        whileTap={{ scale: 0.95 }}
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        )}
      </motion.button>

      {/* Menu Overlay & Content */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="lg:hidden fixed inset-0 z-[1040] bg-black/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <motion.div
              ref={menuRef}
              className="lg:hidden fixed z-[1045] w-[280px]"
              style={{
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 148px)',
                right: '16px',
              }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div
                className="rounded-2xl shadow-2xl overflow-hidden"
                style={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {/* Header */}
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <h3
                    className="text-sm font-semibold"
                    style={{ color: 'var(--color-text)' }}
                  >
                    Quick Actions
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Add items quickly
                  </p>
                </div>

                {/* Actions Grid */}
                <div className="p-2 grid grid-cols-2 gap-1.5">
                  {quickActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <motion.button
                        key={action.label}
                        onClick={action.action}
                        title={action.description}
                        aria-label={action.label}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors"
                        style={{
                          backgroundColor: 'transparent',
                        }}
                        whileHover={{
                          backgroundColor: 'var(--color-card-hover)',
                        }}
                        whileTap={{ scale: 0.97 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: action.bgColor }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: action.color }}
                            strokeWidth={1.75}
                          />
                        </div>
                        <div className="text-center">
                          <p
                            className="text-xs font-medium leading-tight"
                            style={{ color: 'var(--color-text)' }}
                          >
                            {action.label}
                          </p>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export default MobileQuickActions;
